import React from "react"
import { useTranslation } from "react-i18next"
import * as BikeApi from "features/bike/api/BikeApi"

import { useAlert, useAuth } from "hooks"

import FormElement from "components/Form/FormElement"

import { isPhoneValid } from "utils/bikeValidation"
import { isEmailValid } from "utils"
import { getBikeStatusLabelByValue, getLabelFromBikeId, getBikeLabelByText } from "utils/constants"
import { Table } from "features/bike/Partials/Table"
import { Link } from "react-router-dom"
import { privateRoutes } from "router/routes"

const defaultFormEvents = { ready: true }
const defaultFormFields = { search: "" }
const SEARCH_BY_MAIL_KEY_PARAM = "mail"
const SEARCH_BY_PHONE_KEY_PARAM = "phone"

export const FormAdvancedSearchBike = () => {

  const [bikes, setBikes] = React.useState([])
  const [formFields, setFormFields] = React.useState(defaultFormFields)
  const [formEvents, setFormEvents] = React.useState(defaultFormEvents)

  const [apiError, setApiError] = React.useState(null)
  const [errors, setErrors] = React.useState([])
  const [showLocalErrors, setShowLocalErrors] = React.useState(false)

  const { logout } = useAuth()
  const { alert } = useAlert()
  const { t } = useTranslation()

  const columns = React.useMemo(() => [
    {
      Header: t("history.activity_headers.code", { ns: "partner" }).toUpperCase(),
      accessor: "bike_id",
      Cell({ row }) {
        const { bike_id } = row.original
        return <Link
          className="underline"
          to={{ pathname: `${privateRoutes.bike.basePath}/${bike_id}` }}>{bike_id}</Link>
      }
    },
    {
      Header: t("history.activity_headers.brand", { ns: "partner" }).toUpperCase(),
      accessor: "bike_description.brand"
    },
    {
      Header: t("history.activity_headers.model", { ns: "partner" }).toUpperCase(),
      accessor: "bike_description.model"
    },
    {
      Header: t("history.activity_headers.type", { ns: "partner" }).toUpperCase(),
      accessor: "bike_description.gear_type",
      Cell({ row }) {
        const translationKey = getLabelFromBikeId(row.original.bike_description.gear_type_id) ?? getBikeLabelByText(row.original.bike_description.gear_type)
        return <p>{t(translationKey)}</p>
      }
    },
    {
      Header: t("history.activity_headers.vae", { ns: "partner" }).toUpperCase(),
      Cell({ row }) {
        return <p>{row.original.bike_description.is_electric ? t("global.yes") : t("global.no")}</p>
      }
    },
    {
      Header: t("history.activity_headers.color", { ns: "partner" }).toUpperCase(),
      accessor: "bike_description.color"
    },
    {
      Header: t("bike_status", { ns: "bike" }).toUpperCase(),
      accessor: "bike_status",
      Cell({ row }) {
        return <p>{getBikeStatusLabelByValue(row.original.bike_status)}</p>
      }
    },
  ], [t])

  const handleChange = (e) => setFormFields({ search: e.target.value })

  const handleValidation = React.useCallback((name, errs) => {
    setErrors((s) => {
      const cleanErrors = [...s.filter((e) => e.origin !== name)]
      return [
        ...cleanErrors,
        ...errs
      ]
    })
  }, [])

  const handleSubmit = async (event) => {
    event.preventDefault()
    // Validate inputs
    const formReady = !(errors.length > 0)

    if (!formReady) return setShowLocalErrors(true)

    try {
      // hide error
      setShowLocalErrors(false)
      setApiError(null)
      // prevent form to be submitted again
      setFormEvents({ ...formEvents, ready: false })
      // clear bikes data
      setBikes([])
      // get search params
      const search = formFields.search.trim()
      const searchType = isEmailValid(search) ? SEARCH_BY_MAIL_KEY_PARAM : SEARCH_BY_PHONE_KEY_PARAM
      const { res: bikes, getError } = await BikeApi.getBikes(search, searchType)

      if (getError()) throw getError()

      if (!bikes.length)
        // eslint-disable-next-line no-throw-literal
        throw { code: "BikeNotFoundException", message: t("search_error_BikesNotFoundException", { ns: "api" }) }

      setBikes(bikes)
    } catch (error) {
      console.error(error)
      const erroMsg = error?.message?.toString()
      if (erroMsg) alert("error", erroMsg)
      if (["BikeNotFoundException", "InvalidArgument"].includes(error?.code)) setApiError(erroMsg)
      if (["NotAuthorizedException"].includes(error?.code)) logout()
    } finally {
      setFormEvents(s => ({ ...s, ready: true }))
    }
  }

  React.useEffect(function () {
    // hide error on lang change
    setShowLocalErrors(false)
    setApiError(null)
  }, [t])

  return (
    <>
      <div className="l-form-width l-form-width--xl">
        <form>
          <p className="mb-7">
            {t("search_advanced", { ns: "bike" })}
          </p>

          <div className="flex items-center  md:items-start flex-col md:flex-row mb-7 ">

            {/* Bike Code */}
            <div className="flex items-start mr-7">
              <FormElement
                value={formFields.search}
                placeholder="john.doe@domain.com / +33612345678"
                name="search-bike"
                type="text"
                label={t("search_by_mail_or_phone", { ns: "bike" })}
                className=""
                style={{}}
                customRules={[
                  {
                    key: "bikeSearchMailOrPhoneFormat",
                    check: (value) => isEmailValid(value) || isPhoneValid(value),
                    message: t("mail_or_phone", { ns: "form" })
                  },
                ]}
                required={true}
                readonly={false}
                showErrors={showLocalErrors}
                onValidate={handleValidation}
                onChange={handleChange}
              />
            </div>

            <div className="mt-5">
              {formEvents.ready &&
                <button
                  type="submit"
                  className="c-btn c-btn--primary px-12"
                  onClick={handleSubmit}
                >
                  {t("global.validate")}
                </button>
              }
              {!formEvents.ready &&
                <div className="c-spinner"></div>
              }
            </div>
          </div>

          {apiError &&
            <div className="mb-7">
              <p className="text-danger">{apiError}</p>
              <p className="text-danger">{t("search_advanced_global_error", { ns: "bike" })}</p>
            </div>
          }
        </form>
      </div>

      {bikes.length > 0 && (<Table
        columns={columns}
        data={bikes}
        isSortable={false}
        isPaginable={true}
        hasSearchBar={false}
      />)}
    </>
  )
}
